import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { mapWithConcurrencyLimit } from '../../../lib/Concurrency'
import { BudgetContext, SpendContext } from '../../../lib/Context'
import { Reconciliation, Spend } from '../../../lib/Types'
import { BudgetViewMode } from '../lib/Enums/BudgetViewMode'
import { SheetType } from '../lib/Enums/SheetType'
import { useBudget } from '../lib/Hooks/Budget'
import { useExpenses, useUpdateSpend } from '../lib/Hooks/Spends'
import { isPaymentOverdue } from '../lib/Payment'
import SheetTitle from '../SheetTitle'
import Welcome from '../WelcomeBudget'
import './Budget.css'
import BudgetExpenses from './BudgetExpenses'
import BudgetIncome from './BudgetIncome'
import BudgetMenu from './BudgetMenu'
import BudgetSavings from './BudgetSavings'
import BudgetReconciliation from './Reconciliation/BudgetReconciliation'

const BudgetComponent = () => {
  const { t } = useTranslation(['budget'])
  const [viewMode, setViewMode] = useState<BudgetViewMode>(
    BudgetViewMode.EXPENSE,
  )
  const [reconciliationYears, setReconciliationYears] = useState<number[]>([])
  const [reconciliationYear, setReconciliationYear] = useState<number>()
  const [reconciliationToEdit, setReconciliationToEdit] = useState<{
    reconciliation: Reconciliation
    spend: Spend
  } | null>(null)
  const urlParams = new URLSearchParams(window.location.search)

  const { data: budget } = useBudget()
  const { data: spends } = useExpenses(budget!)
  const { mutateAsync: updateSpend } = useUpdateSpend(budget!)

  useEffect(() => {
    if (budget) {
      const yearsToSet = []
      const latestYearForReconciliation = new Date().getFullYear()
      let currentYear = new Date(budget?.created_date).getFullYear()

      while (currentYear <= latestYearForReconciliation) {
        yearsToSet.unshift(currentYear)
        currentYear++
      }

      setReconciliationYears(yearsToSet)
    }
  }, [budget])

  /**
   * Iterate a set of spends to see if the order they are represented are identical to the order from the DB.
   *
   * @param {array} spendsToSave
   *
   * @returns {bool}
   *   Whether anything was saved or not
   */
  const saveOrder = async (spendsToSave: Spend[]): Promise<boolean> => {
    const spendsToUpdate: { [k: number]: Spend } = {}

    // Iterate all spends to update the order
    spendsToSave.forEach((item, index) => {
      if (item.order !== index) {
        item.order = index
        spendsToUpdate[item.id] = item
      }
    })

    const spendsToUpdateArr: Spend[] = Object.values(spendsToUpdate)

    await mapWithConcurrencyLimit(spendsToUpdateArr, 4, (item) =>
      updateSpend(item),
    )

    return spendsToUpdateArr.length > 0
  }

  if (!budget) {
    return <p>{t('budget:budgetView.noBudget')}</p>
  }

  return (
    <>
      <Welcome
        sheet={budget}
        show={
          isPaymentOverdue(budget) ||
          (!spends!.length &&
            !budget.is_paid &&
            !budget.has_accepted_payment &&
            !urlParams.get('hide'))
        }
      />
      <BudgetContext.Provider
        value={{
          setViewMode,
          setReconciliationToEdit,
          reconciliationToEdit,
          reconciliationYear,
        }}
      >
        <SpendContext.Provider value={{ saveOrder }}>
          <SheetTitle
            type={SheetType.BUDGET}
            title={budget.name || t('budget:budgetView.defaultTitle')}
            url={`/budget/${budget.id}`}
          />

          {budget.description && (
            <p className='italic mb-2'>{budget.description}</p>
          )}
          <BudgetMenu viewMode={viewMode} />
          <div className='overflow-x-auto'>
            <div className='flex'>
              <div
                className={`py-2 px-5  ${viewMode === BudgetViewMode.EXPENSE ? 'underline bg-gray-300' : 'hover:underline cursor-pointer bg-gray-100'}`}
                onClick={() => setViewMode(BudgetViewMode.EXPENSE)}
              >
                {t('budget:budgetView.expenses')}
              </div>
              <div
                className={`py-2 px-5  ${viewMode === BudgetViewMode.INCOME ? 'underline bg-gray-300' : 'hover:underline cursor-pointer bg-gray-100'}`}
                onClick={() => setViewMode(BudgetViewMode.INCOME)}
              >
                {t('budget:budgetView.income')}
              </div>
              <div
                className={`py-2 px-5  ${viewMode === BudgetViewMode.SAVING ? 'underline bg-gray-300' : 'hover:underline cursor-pointer bg-gray-100'}`}
                onClick={() => setViewMode(BudgetViewMode.SAVING)}
              >
                {t('budget:budgetView.savings')}
              </div>
              {reconciliationYears.length > 0 && (
                <>
                  <div className={'py-2 px-5 bg-theme-cyan'}>
                    {t('budget:budgetView.reconciliation')}
                  </div>
                  {reconciliationYears.map((year) => (
                    <div
                      key={year}
                      className={`py-2 px-5  ${viewMode === BudgetViewMode.RECONCILIATION && reconciliationYear === year ? 'underline bg-gray-300' : 'hover:underline cursor-pointer bg-gray-100'}`}
                      onClick={() => {
                        setViewMode(BudgetViewMode.RECONCILIATION)
                        setReconciliationYear(year)
                      }}
                    >
                      {year}
                    </div>
                  ))}
                </>
              )}
            </div>
            {viewMode === BudgetViewMode.EXPENSE && <BudgetExpenses />}
            {viewMode === BudgetViewMode.RECONCILIATION && (
              <BudgetReconciliation />
            )}
            {viewMode === BudgetViewMode.INCOME && <BudgetIncome />}
            {viewMode === BudgetViewMode.SAVING && <BudgetSavings />}
          </div>
        </SpendContext.Provider>
      </BudgetContext.Provider>
    </>
  )
}

export default BudgetComponent
